import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FilePdfIcon } from '@phosphor-icons/react';
import {
    usePlandokumenterForBehandling,
    usePlandokumenterForDispensasjon,
    useFetchPlandokumentAttachmentUrl,
} from '@origo/planregister';
import { Button, EmptyState, Loader, Sheet } from '@origo/origo-ui';
import DocumentList from './DocumentList';
import NewDocumentDialog from './NewDocumentDialog';
import DeleteDocumentDialog from './DeleteDocumentDialog';
import LinkExistingDocumentsDialog from './LinkExistingDocumentsDialog';
import UnlinkDocumentDialog from './UnlinkDocumentDialog';
import { useDocumentManagement } from '../hooks/useDocumentManagement';
import { sortNewestFirst, toDocumentItem } from '../../../mappers/toDocuments';
import styles from './LinkedDocumentsSheet.module.css';

/** Hva sheeten viser dokumenter for. */
export type LinkedDocumentsScope =
    | { kind: 'behandling'; planbehUid: string }
    | { kind: 'dispensasjon'; dispensasjonId: string };

interface LinkedDocumentsSheetProps {
    napId: string;
    /** `null` holder sheeten lukket (Sheet forblir montert for korrekt fokus-håndtering). */
    scope: LinkedDocumentsScope | null;
    onClose: () => void;
}

/** i18n-prefiks for tittel/tom-tilstand per scope. */
const I18N_PREFIX: Record<LinkedDocumentsScope['kind'], string> = {
    behandling: 'processings.documents',
    dispensasjon: 'dispensations.documents',
};

/** Viser plandokumenter knyttet til én planbehandling (jf. spec F3.1.2) eller én
 *  dispensasjon (F12.4), med samme opprett/slett-håndtering som den plan-brede
 *  Plandokumenter-fanen. */
const LinkedDocumentsSheet: React.FC<LinkedDocumentsSheetProps> = ({ napId, scope, onClose }) => {
    const { t } = useTranslation();
    const planbehUid = scope?.kind === 'behandling' ? scope.planbehUid : undefined;
    const dispensasjonId = scope?.kind === 'dispensasjon' ? scope.dispensasjonId : undefined;
    // Kun den aktive queryen er `enabled` — den andre får `undefined` og kjører ikke.
    const behandlingQuery = usePlandokumenterForBehandling(planbehUid);
    const dispensasjonQuery = usePlandokumenterForDispensasjon(dispensasjonId);
    const { data, isLoading } = dispensasjonId ? dispensasjonQuery : behandlingQuery;
    const fetchAttachmentUrl = useFetchPlandokumentAttachmentUrl();
    const prefix = I18N_PREFIX[scope?.kind ?? 'behandling'];

    // Nyeste øverst, samme sortering som Plandokumenter-fanen.
    const items = useMemo(() => sortNewestFirst(data ?? []).map(doc => toDocumentItem(doc, t)), [data, t]);

    const dm = useDocumentManagement({ items, data, napId, planbehUid, dispensasjonId });

    const isEmpty = items.length === 0;
    // Knytting av eksisterende dokumenter gjelder kun behandlinger.
    const canLinkExisting = planbehUid !== undefined;

    const actions = (
        <div className={styles.actions}>
            <Button variant="secondary" size="md" onClick={dm.openNew}>
                {t('documents.newDocument')}
            </Button>
            {canLinkExisting && (
                <Button variant="secondary" size="md" onClick={dm.openLinkExisting}>
                    {t('processings.documents.linkExisting.button')}
                </Button>
            )}
        </div>
    );

    return (
        <Sheet
            open={scope !== null}
            onClose={onClose}
            title={t(`${prefix}.title`)}
        >
            <div className={styles.body}>
                {/* Behandling: handlingene står øverst også når listen er tom. */}
                {!isLoading && (canLinkExisting || !isEmpty) && actions}
                {isLoading ? (
                    <Loader label={t('common.loading')} />
                ) : isEmpty ? (
                    <EmptyState
                        icon={<FilePdfIcon size={32} weight="duotone" />}
                        title={t(`${prefix}.empty.title`)}
                        description={t(`${prefix}.empty.description`)}
                        action={
                            canLinkExisting ? undefined : (
                                <Button variant="primary" size="lg" onClick={dm.openNew}>
                                    {t('documents.newDocument')}
                                </Button>
                            )
                        }
                    />
                ) : (
                    <DocumentList
                        items={items}
                        onOpen={async id => {
                            const doc = data?.find(d => String(d.objectId) === id);
                            if (!doc?.referansedokumentfil) return;
                            const blobUrl = await fetchAttachmentUrl(doc.referansedokumentfil);
                            if (blobUrl) window.open(blobUrl, '_blank', 'noopener,noreferrer');
                        }}
                        rowAction={canLinkExisting ? 'unlink' : 'delete'}
                        onAction={(action, id) => (action === 'unlink' ? dm.requestUnlink(id) : dm.requestDelete(id))}
                    />
                )}
            </div>

            <NewDocumentDialog
                open={dm.newDocument.open}
                values={dm.newDocument.values}
                file={dm.newDocument.file}
                fieldErrors={dm.newDocument.fieldErrors}
                fileError={dm.newDocument.fileError}
                onChange={dm.changeNew}
                onFileChange={dm.changeNewFile}
                onClose={dm.closeNew}
                onSave={dm.saveNew}
                isSaving={dm.newDocument.isSaving}
            />

            {canLinkExisting && (
                <LinkExistingDocumentsDialog
                    open={dm.linkExistingOpen}
                    napId={napId}
                    onClose={dm.closeLinkExisting}
                    onConfirm={dm.confirmLinkExisting}
                    isSaving={dm.isLinking}
                />
            )}

            {/* Behandling: radhandlingen er «Fjern», så sletting er kun aktuelt for dispensasjon. */}
            {canLinkExisting ? (
                <UnlinkDocumentDialog
                    pendingUnlink={dm.pendingUnlink}
                    onConfirm={dm.confirmUnlink}
                    onCancel={dm.cancelUnlink}
                    isUnlinking={dm.isUnlinking}
                />
            ) : (
                <DeleteDocumentDialog
                    pendingDelete={dm.pendingDelete}
                    blokkering={dm.deleteBlokkering}
                    avventerSjekk={dm.deleteAvventerPlan}
                    onConfirm={dm.confirmDelete}
                    onCancel={dm.cancelDelete}
                    isDeleting={dm.isDeleting}
                />
            )}
        </Sheet>
    );
};

export default LinkedDocumentsSheet;
